
import router from '@/router';

import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { isCartBlockedSession } from '@/utils/roles';
import { Debounce } from 'vue-debounce-decorator';
import CPRadioGroup from '@/components/open-account/input/CPRadioGroup.vue';
import CPLoader from '@/components/globals/CPLoader.vue';
import CPTooltipAdvanced from '@/components/globals/CPTooltipAdvanced.vue';

import CPOptionDropdown from '@/components/open-account/input/CPOptionDropdown.vue';
import CPModal from '@/components/globals/CPModal.vue';
import { filter } from 'vue/types/umd';
import { BasketDetails } from '@/definitions/definitions';


export interface Documents {
  EnvelopeID: string | null;
  EnvelopeURL: string | null;
  IsSuccessful: boolean | null;
  Message: string | null;
  SoldFunds: any | null;
  PDFDetails: PDFDetails;
  PurchasedFunds: any | null;
}
export interface PDFDetails {
  PDFUrls: PDFUrl[];
  SignerDetails: Signee[];
}
export interface PDFUrl {
  FileName: string;
  PDFUrl: string;
}

export interface Signee {
  SignerName: string;
  SignerEmailAddress: string;
  SignerRole: string;
  SignerOrder: number;
  IsCC: boolean;
}


export interface TradeProcessRequest {
  CheckoutDocusign: boolean;
  CheckoutOnline: boolean;
  PurchasedFunds: Fund[];
  SoldFunds: Fund[];
  UserInfo: UserInfo;

}
export interface Fund {
  Alert: Alert;
  Amount: number;
  CartId: number;
  CashRebate: any | null;
  CashRebateType: any | null;
  CheckoutDocusign: boolean;
  CheckoutOnline: boolean;
  Currency: string;
  DiscountPercentage: number;
  Forex: Forex[];
  ID: number;
  ISIN: string;
  NAV: number;
  Name: string;
  Price: number;
  ProductType: string;
  TradeOptions: string;
  TradeValue: number;
  Units: number;
  isFilled: boolean;

}
export interface Forex {
  BuyAmount: number;
  BuyCurrency: string;
  SellAmount: number;
  SellCurrency: string;
}

export interface Alert {
  AutoResetGainFallValue: number;
  CartId: number;
  FallRate: number;
  FallSelection: string;
  GainRate: number;
  GainSelection: string;
}

export interface UserInfo {
  ActiveBostonId: number;
  CPAccount: string;
  CurrentUserBostonId: number;
  MoventumNumber: number;
  PlatformId: number;
  TotalAccountPosition: string;
  UserInfoId: number;
  WhiteLabelCode: string;
  WhiteLabelId: string;
}


@Component({
  components: {
    CPRadioGroup,
    CPLoader,
    CPTooltipAdvanced,
    CPOptionDropdown,
    CPModal,
  },
})
export default class CPCart extends Vue {
  get isCartBlocked(): boolean {
    return isCartBlockedSession(this.$store.state.account.details);
  }


  // Show Checkout
  public get accountIds(): boolean {
    return this.$store.state.account.accountSummary.map((account: any) => account.value);
  }
  // Show Checkout
  public get checkout(): boolean {
    return this.$store.state.baskets.checkoutActive;
  }

  // Callback for resolved trade
  public get tradeResolved() {
    return this.$store.state.baskets.tradeResolved;
  }

  public get availableCash() {
    return parseFloat(this.$store.state.account.details.activeAccount.AvailableBalance);
  }

  public get amountInAccountCurrency() {
    if (this.checkoutProduct.ProductType === 'Hybrid Structured Instruments' || this.checkoutProduct.ProductType === 'Fixed Income Bond') {
      return parseFloat((this.convertCurrencyToAny((1 * this.checkoutProduct.TradeValue), this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR, this.accountCurrency.Rate_EUR)).toFixed(4));
    } else if (this.checkoutProduct.ProductType === 'Actively Managed Certificates') {
      return parseFloat((this.convertCurrencyToAny(this.getAmcCashValue(this.checkoutProduct), this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR, this.accountCurrency.Rate_EUR)).toFixed(4));
    } else {
      if (this.checkoutProduct.TradeOptions) {
        return parseFloat((this.convertCurrencyToAny((this.checkoutProduct.TradeValue), this.accountCurrency.Rate_EUR, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR)).toFixed(4));
      } else {
        return parseFloat((this.convertCurrencyToAny(
          (this.checkoutProduct.NAV * this.checkoutProduct.TradeValue),
           this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR,
           this.accountCurrency.Rate_EUR)).toFixed(4));
      }
    }
  }

  public get exchangeRate() {
    return (this.accountCurrency.Rate_EUR / this.productExchangeRate.Rate_EUR);
  }

  public get amountRemaining(): number | null {
    if (this.checkoutProduct) {
      if (this.checkoutProduct && this.checkoutProduct.ProductType && this.checkoutProduct.ProductType === 'Hybrid, Structured Instruments' ||
      this.checkoutProduct && this.checkoutProduct.ProductType && this.checkoutProduct.ProductType === 'Fixed Income Bond') {
        return parseFloat((this.checkoutProduct.TradeValue - this.amount).toFixed(2));
      } else if (this.checkoutProduct && this.checkoutProduct.ProductType && this.checkoutProduct.ProductType === 'Actively Managed Certificates') {
        return parseFloat((this.getAmcCashValue(this.checkoutProduct) - this.amount).toFixed(2));
      } else {
        if (this.checkoutProduct.TradeOptions === 'Amount') {
          return this.checkoutProduct.TradeValue - this.amount;
        } else {
          return parseFloat(((this.checkoutProduct.TradeValue * this.checkoutProduct.NAV) - this.amount).toFixed(2));
        }
      }
    } else {
      return null;
    }
  }

  public get productExchangeRate() {
    return this.$store.state.currencyRates.filter((x: any) => {
      if (x.Currency === this.checkoutProduct.Currency) {
        return x;
      }
    })[0];
  }

  public get accountCurrency() {
    return this.$store.state.currencyRates.filter((x: any) => {
      if (x.Currency === this.$store.state.account.details.activeAccount.currency) {
        return x;
      }
    })[0];
  }

  public get sortedForex() {
    const forex: any[] = [];

    this.sortedCashPosition.forEach((item: any) => {
      if (item.value > 0) {
        forex.push(
          {
            SellCurrency: item.Currency,
            SellAmount: parseFloat(item.value.toFixed(4)),
            BuyCurrency: item.productCurrency,
            BuyAmount: item.valueInProductCurrency,
          },
        );
      }
    });
    return forex;
  }

  // Documents and Docusign info that come back from API
  public get documents(): Documents {
    return this.$store.state.baskets.documents;
  }

  public get cart() {
    return this.$store.state.baskets.cart.items;
  }

  public get cashAmountRemaining(): any {
    if (this.checkoutProduct.TradeOptions === 'Amount') {
      let total = 0;

      this.sortedCashPosition.forEach((item: any) => {
        if (item.value) {
          total += this.convertAmountToItemCurrency(this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR, item);
        }
      });
      if (this.amountRemaining && typeof this.amountRemaining === 'number') {
        return Math.round(((this.amountRemaining * 100) / 100) - total);
      } else {
        return 'n/a';
      }
    } else {
      let total = 0;
      this.sortedCashPosition.forEach((item: any) => {
        if (item.value) {
          total += this.convertAmountToItemCurrency(this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR, item);
        }
      });
      if (this.amountRemaining) {
        return parseFloat((((this.amountRemaining * 100) / 100) - parseFloat(total.toFixed(2))).toFixed(2));
      } else {
        return 'n/a';
      }

    }
  }

  // Checks the sortedCashPosition array and compares it to the amount added to determine if next button should be disabled.
  public get overIndividualAvailableCurrencyAmount() {
    return this.sortedCashPosition.some((item) => item.value !== 0 && item.value > item.availableCash);
  }

  public get aggregateCurrencyOver(): Array<{ currency: string; required: number; available: number }> {
    const totals: Record<string, number> = {};
    const currentId = this.checkoutProduct?.ID;
    const finalized: any[] = this.tradeProcessRequest.PurchasedFunds || [];
    const currentAlreadyFinalized = finalized.some((p: any) => p.ID === currentId);

    finalized.forEach((item: any) => {
      (item.Forex || []).forEach((f: any) => {
        totals[f.SellCurrency] = (totals[f.SellCurrency] || 0) + (f.SellAmount || 0);
      });
    });

    if (!currentAlreadyFinalized) {
      this.sortedCashPosition.forEach((row: any) => {
        if (row.value > 0) {
          totals[row.Currency] = (totals[row.Currency] || 0) + row.value;
        }
      });
    }

    const result: Array<{ currency: string; required: number; available: number }> = [];
    Object.keys(totals).forEach((cur) => {
      const required = totals[cur];
      const available: number = (this.cashPosition && this.cashPosition[cur]) || 0;
      if (required > available) {
        result.push({ currency: cur, required, available });
      }
    });
    return result;
  }

  public get hasAggregateCurrencyOver(): boolean {
    return this.aggregateCurrencyOver.length > 0;
  }

  public get aggregateCurrencyWarningLines(): string[] {
    const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return this.aggregateCurrencyOver.map((item) =>
      `<strong>${item.currency}</strong> requires <strong>${fmt(item.required)}</strong>, available cash is <strong>${fmt(item.available)}</strong>.`,
    );
  }

  public get hasAlerts() {
    return this.checkoutProduct.ProductType === 'Hybrid, Structured Instruments' ||
    this.checkoutProduct.ProductType === 'Fixed Income Bond' ||
    this.checkoutProduct.ProductType === 'Actively Managed Certificates' ? false : true;
  }

  public get hasBuyRights() {
    return this.$store.state.account.details.activeAccount.AllowedToPurchase;
  }
  public get activeAccountId() {
    return this.$store.state.account.details.activeAccountId;
  }

  public get cartList() {
    return this.$store.state.baskets.cart.items.CartInformation;
  }
  public get cashPosition() {
    return this.$store.state.cashPosition;
  }

  public get fetching() {
    return this.$store.state.baskets.cart.fetching;
  }

  // Checks buy orders array for any products that cannot be checked out online
  public get manualCheckoutOnly() {
    const buy = this.activeBuyOrders.some((item: any) => item.CheckoutOnline === false);
    const sell = this.activeSellOrders.some((item: any) => item.CheckoutOnline === false);
    if (buy || sell) {
      return true;
    } else {
      return false;
    }
  }

  public get hasNotesAndSigned() {
    let isTrue = false;
    if (
    this.activeBuyOrders.some((item: any) => item.ProductType === 'Hybrid, Structured Instruments') &&
    this.$store.state.account.details.transactionFeeSigned &&
    this.hasBuyRights) {
      isTrue = true;
    }
    if (this.activeBuyOrders.some((item: any) => item.CheckoutOnline === false)) {
      isTrue = false;
    }
    return isTrue;
  }

  public get tradeProcessRequest(): TradeProcessRequest {
    const tpl: any = {
      PurchasedFunds: [],
      SoldFunds: [],
      UserInfo: {
        UserInfoId: parseFloat(this.$store.state.account.details.userId),
        ActiveBostonId: parseFloat(this.$store.state.account.details.bostonId),
        CurrentUserBostonId: window.sessionStorage.getItem('consultantBostonID') &&
        window.sessionStorage.getItem('consultantBostonID') !== '' ?
        window.sessionStorage.getItem('consultantBostonID') :
        parseFloat(this.$store.state.account.details.bostonId),
        PlatformId: parseInt(this.$store.state.account.details.activeAccount.id, 10),
        MoventumNumber: parseInt(this.$store.state.account.details.activeAccount.moventumId, 10),
        CPAccount: this.$store.state.account.username,
        WhiteLabelCode:
          this.$store.state.account.details.companyCode ||
          this.$store.state.account.username.substring(0, 3),
        WhiteLabelId: this.$store.state.account.details.whiteLabelId,
        TotalAccountPosition: this.$store.state.account.details.activeAccount.securityAndCashPosition,
      },
      CheckoutOnline: false,
      CheckoutDocusign: false,
    };
    return tpl;
  }

  public get validNumberOfBuyItems() {
    return this.activeBuyOrders.length < 9 ? true : false;
  }

  public get loading(): boolean {
    return this.$store.state.isCartLoading;
  }

  public set loading(value: boolean) {
    this.$store.commit('setIsCartLoading', value);
  }

  // Transaction Successful (shows message on step 4)
  public success: boolean = false;

  // Variables for active product in 3-step process
  public checkoutProduct: any | null = null;
  public activeCheckoutStep: number = 1;
  public activeProductCount: number = 1;
  public prevCheckoutStep: number = 0;
  public amount = 0;

  // Alerts
  public gainTarget: number = 5;
  public alertGainAction: any = 1;
  public afterResetGainTarget: number = 5;
  public fallTarget: number = 5;
  public alertFallAction: any = 1;
  public afterResetFallTarget: number = 5;

  // Used to keep same Alert values if Checkout has multiple products
  public useSameValues: boolean = false;

  // Used to display success page for different checkout types
  public checkoutType: string | null = null;

  public buyItems: any[] = [];
  public sellItems: any[] = [];
  public activeBuyOrders: any[] = [];
  public activeBuyTotal: number = 0;
  public activeSellOrders: any[] = [];
  public activeSellTotal: number = 0;

  // animation toggles for updated amt
  public flashTotalBuy: boolean = false;
  public flashTotalSell: boolean = false;

  public sortedCashPosition: any[] = [];

  // agree to terms
  public agreeToTerms: boolean = false;

  public hasRebate: boolean = false;

  public itemsToRemove: any[] = [];

  public notesUnableToProceedOnlineDisclaimer: boolean = false;

  public hasInvalidNotes() {
    let isTrue = false;
    if (
      this.activeBuyOrders.some((item: any) => item.ProductType === 'Hybrid, Structured Instruments') &&
      !this.$store.state.account.details.transactionFeeSigned
      ) {
      isTrue = true;
    }
    if (
      this.activeBuyOrders.some((item: any) => item.CheckoutOnline === false) &&
      this.activeBuyOrders.some((item: any) => item.ProductType === 'Hybrid, Structured Instruments')
      ) {
      isTrue = true;
    }
    this.notesUnableToProceedOnlineDisclaimer = isTrue;
  }

  public isBondOrNote(product: BasketDetails): boolean {
    return ['Hybrid, Structured Instruments', 'Fixed Income Bond', 'Actively Managed Certificates'].some((x) => x.includes(product.ProductType));
  }

  @Watch('activeAccountId')
  public getUpdatedCart() {
    this.$store.dispatch('getCart');
    this.$store.dispatch('getCashPosition');
  }

  public goToClientList() {
    // @ts-ignore - tell typscript to ignore this line as history is a private api and therefore doesnt appear on the typings
    if (router.history.current.name !== 'my-account') {
      router.push('/my-account');
    }
    setTimeout(() => {
      const el = document.getElementsByClassName('cp-client-directory')[0];
      this.$store.dispatch('scrollToActiveSection', el);
    }, 800);
    this.$store.commit('updateFocusElement', 'client-list');
    this.close();
  }

  // Go to search section
  public search() {
    // @ts-ignore - tell typscript to ignore this line as history is a private api and therefore doesnt appear on the typings
    if (router.history.current.name !== 'Home') {
      router.push('/');
    }
    this.$store.commit('updateFocusElement', 'search');
    this.$store.dispatch('setActiveTab', 'search-results');
    this.close();
  }

  public openSell() {
    // @ts-ignore - tell typscript to ignore this line as history is a private api and therefore doesnt appear on the typings
    if (router.history.current.name !== 'My Account') {
      this.$router.push('/my-account');
    }
    this.$store.commit('updateFocusElement', 'transactions');
    this.close();
  }

  // Watches for when a trade is resolved and returns to cart from checkout
  @Watch('tradeResolved', { deep: true })
  public backToCart() {
    this.loading = false;
    this.success = false;
    if (this.tradeResolved === false || this.tradeResolved === null) {
      this.loading = false;
      this.success = false;
    } else {
      this.success = true;
      setTimeout(() => {
        this.success = false;
      }, 2000);
    }
  }

  // Pay full amount in selected currency
  public payAll(item: any) {
    // Get index of current currency line item;
    const index = this.sortedCashPosition.findIndex((currency) => currency.Currency === item.Currency);

    // Clear all existing values to zero
    this.sortedCashPosition.forEach((cashPosition) => { cashPosition.valueInProductCurrency = 0; });

    // Run payAll logic for Notes and Bonds
    if (this.checkoutProduct.ProductType === 'Hybrid, Structured Instruments' || this.checkoutProduct.ProductType === 'Fixed Income Bond') {
      this.sortedCashPosition[index].value = parseFloat((this.convertCurrencyToAny((1 * this.checkoutProduct.TradeValue),
        item.Rate_EUR, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR)).toFixed(4));
      this.sortedCashPosition[index].valueInProductCurrency = parseFloat((1 * this.checkoutProduct.TradeValue).toFixed(4));
    } else if (this.checkoutProduct.ProductType === 'Actively Managed Certificates') {
      const amcCashValue = this.getAmcCashValue(this.checkoutProduct);
      this.sortedCashPosition[index].value = parseFloat((this.convertCurrencyToAny(amcCashValue,
        item.Rate_EUR, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR)).toFixed(4));
      this.sortedCashPosition[index].valueInProductCurrency = parseFloat(amcCashValue.toFixed(4));
    } else {
      this.sortedCashPosition[index].valueInProductCurrency = this.checkoutProduct.TradeOptions === 'Amount' ?
        parseFloat(this.checkoutProduct.TradeValue.toFixed(4))
      : parseFloat((this.checkoutProduct.NAV * this.checkoutProduct.TradeValue).toFixed(4));
      this.sortedCashPosition[index].value = this.checkoutProduct.TradeOptions === 'Amount' ?
      parseFloat(
        (this.convertCurrencyToAny((this.checkoutProduct.TradeValue),
        item.Rate_EUR, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR)).toFixed(4),
      ) :
      parseFloat(
        (this.convertCurrencyToAny((this.checkoutProduct.NAV * this.checkoutProduct.TradeValue),
        item.Rate_EUR, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR)).toFixed(4),
      );
    }
  }

  // Closes Cart/Checkout
  public close() {
    this.$store.commit('toggleCart', false);
  }

  public remove(item: any) {
    const obj = {
      name: item.Name,
      cartID: item.ID,
      roleInfoId: (item.UserInfoId).toString(),
    };
    this.$store.dispatch('deleteCart', obj);
  }

  public editBuyItem(item: any) {
    this.$store.dispatch('updateBuyBasketDetails', item);
    this.$store.commit('toggleBuyBasket', true);
    this.$store.commit('buyBasketEdit', true);
    this.$store.commit('buyBasketEditDetails', item);
  }

  public editSellItem(item: any) {
    this.$store.dispatch('updateSellBasketDetails', item);
    this.$store.commit('toggleSellBasket', true);
    this.$store.commit('sellBasketEdit', true);
    this.$store.commit('sellBasketEditDetails', item);
  }

  public hasSellRights() {
    return this.$store.state.account.details.activeAccount;
  }

  @Watch('cartList')
  public getBuySellItems() {
    if (!this.cartList) return;
    const buyItems: any[] = this.cartList.filter((item: any) => item.TransactionType === 'Purchase');
    buyItems.forEach((item: any) => {
      this.$set(item, 'checkoutActive', false);
      item.checkoutActive = false;
    });
    this.buyItems = buyItems;
    const sellItems: any[] = this.cartList.filter((item: any) => item.TransactionType === 'Sell');
    sellItems.forEach((item: any) => {
      this.$set(item, 'checkoutActive', false);
      item.checkoutActive = false;
    });
    this.sellItems = sellItems;
  }

  @Watch('buyItems', { deep: true })
  public getActiveBuyOrders() {
    this.activeBuyOrders = this.buyItems.filter((item) => item.checkoutActive);
    let total = 0;
    this.activeBuyOrders.forEach((item: any) => {
      total += this.convertCurrency(item);
    });
    this.activeBuyTotal = parseFloat(total.toFixed(2));
    this.flashTotalBuy = true;
    setTimeout(() => {
      this.flashTotalBuy = false;
    }, 450);
  }

  @Watch('activeBuyOrders', {deep: true})
  public checkNoteValidity() {
    this.hasInvalidNotes();
  }


  @Watch('sellItems', { deep: true })
  public getActiveSellOrders() {
    this.activeSellOrders = this.sellItems.filter((item) => item.checkoutActive);
    let total = 0;
    this.activeSellOrders.forEach((item: any) => {
      total += this.convertCurrency(item);
    });
    this.activeSellTotal = total;
    this.flashTotalSell = true;
    setTimeout(() => {
      this.flashTotalSell = false;
    }, 450);
  }

  @Watch('cashPosition')
  public configureCashPosition() {
    Object.keys(this.cashPosition).forEach((k) => this.sortedCashPosition.push(
      {
        Currency: k,
        availableCash: this.cashPosition[k],
        Rate_EUR: this.findCurrencyRate(k).Rate_EUR,
        value: 0,
        valueInProductCurrency: 0,
        productCurrency: '',
      },
    ));
  }

  @Watch('sortedCashPosition', { deep: true })
  public updateSortedCashPosition() {
    if (this.checkoutProduct) {
      this.sortedCashPosition.forEach((item: any) => {
        item.productCurrency = this.checkoutProduct.Currency;
        item.value = parseFloat((this.convertCurrencyToAny(item.valueInProductCurrency, this.findCurrencyRate(this.checkoutProduct.Currency).Rate_EUR, item.Rate_EUR)).toFixed(4));
      });
    }
  }

  public setRebate(val: boolean) {
    this.hasRebate = val;
  }

  public getAmcCashValue(item: any): number {
    const amcProduct = (this.$store.state.products.activelyManagedCertificates || []).find((p: any) => p.ISIN === item.ISIN);
    const nominal = item.NAV || amcProduct?.Nominal || 1;
    return nominal * item.TradeValue;
  }

  // convert item value to account currency value
  public convertCurrency(item: any): number {
    let total = 0;
    if (item.ProductType === 'Hybrid, Structured Instruments' || item.ProductType === 'Fixed Income Bond') {
      total = (item.TradeValue) * this.findCurrencyRate(item.Currency).Rate_EUR;
    } else if (item.ProductType === 'Actively Managed Certificates') {
      total = this.getAmcCashValue(item) * this.findCurrencyRate(item.Currency).Rate_EUR;
    } else {
      if (item.TradeOptions === 'SellAll') {
        total = item.CurrentValue * this.findCurrencyRate(item.Currency).Rate_EUR;
      } else if (item.TradeOptions === 'Amount') {
        total = item.TradeValue * this.findCurrencyRate(item.Currency).Rate_EUR;
      } else {
        total = (item.NAV * item.TradeValue) * this.findCurrencyRate(item.Currency).Rate_EUR;
      }
    }
    total = (total * 1) / this.accountCurrency.Rate_EUR;
    return total;
  }

  // convert item value to account currency value
  // Item Currency = actual item
  // Incoming item = currency
  public convertAmountToItemCurrency(itemCurrency: any, incomingItem: any): number {
    let total = 0;
    total = incomingItem.value * incomingItem.Rate_EUR;
    total = (total * 1) / itemCurrency;
    return total;
  }

  public convertCurrencyToAny(incomingAmount: number, incomingRate: number, outgoingRate: number) {
    let total = 0;
    total = incomingAmount * incomingRate;
    total = (total * 1) / outgoingRate;
    return total;
  }

  // function to find currency object from string based list.
  public findCurrencyRate(currency: any) {
    const updatedRefObj = this.$store.state.currencyRates.filter((x: any) => {
      return x.Currency === currency;
    });
    return updatedRefObj[0];
  }

  public startCheckout(item: any) {
    this.checkoutProduct = item;
    if (this.checkoutProduct.CashRebate > 0) {
      this.hasRebate = true;
    }
    this.$store.commit('setCheckoutActive', true);
    this.$store.commit('updateTradeResolved', null);
  }

  public startSellCheckout(item: any) {
    this.$store.commit('setCheckoutActive', true);
    this.activeCheckoutStep = 6;
    this.$store.commit('updateTradeResolved', null);
  }

  // Close Checkout and return to Cart. Resets all values back to default.
  public closeCheckout() {
    this.checkoutProduct = null;
    this.tradeProcessRequest.PurchasedFunds = [];
    this.tradeProcessRequest.SoldFunds = [];
    this.resetCheckoutProduct(false);
    this.agreeToTerms = false;
    this.$store.commit('setCheckoutActive', false);
    this.$store.commit('updateTradeResolved', null);
    this.activeCheckoutStep = 1;
    this.prevCheckoutStep = 0;
    this.activeProductCount = 1;
    this.$store.commit('updateDocuments', null);
    this.activeBuyOrders = [];
    this.activeSellOrders = [];
    this.$store.dispatch('getCart');
  }

  // New Buy Order function that takes multiple products
  // This can be used to checkout manually (pdf), via Docusign (email) or automatically online
  @Debounce(1000)
  public placeMultipleBuyOrder(online: boolean, type: string) {
    if (this.hasAggregateCurrencyOver) {
      this.$store.dispatch('createLog', `Blocked submission: aggregate currency over-commitment: ${JSON.stringify(this.aggregateCurrencyOver)}`);
      return;
    }
    this.loading = true;
    const availableCash = this.$store.state.account.details?.activeAccount?.AvailableBalance;
    const pendingOrdersAmount = this.$store.state.account.details?.activeAccount?.PendingOrderAmount;
    this.$store.dispatch('createLog', `Executing placeMultipleBuyOrder...AvailableCash=${availableCash} PendingOrdersAmount=${pendingOrdersAmount}`);
    this.$store.dispatch('getPendingOrders').then(() => {
      const tradeProcessRequest: any = this.tradeProcessRequest;
      // set type
      this.checkoutType = type;
      if (online) {
        if (this.manualCheckoutOnly) {
          this.$store.dispatch('createLog', 'Online checkout blocked (manualCheckoutOnly). Forcing CheckoutOnline=false.');
          tradeProcessRequest.CheckoutOnline = false;
          this.loading = false;
          return;
        }
        tradeProcessRequest.CheckoutOnline = true;
      }
      if (this.checkoutType === 'docusign') { tradeProcessRequest.CheckoutDocusign = true; }
      // this.tradeProcessRequest.PurchasedFunds = this.activeBuyOrders;
      let ISIN = '';
      if (this.$store.state.account.pendingOrders && this.$store.state.account.pendingOrders.length > 0) {
        for (const [key, value] of Object.entries(this.$store.state.account.pendingOrders[0].PendingOrdersList)) {
          try {
              this.$store.state.account.pendingOrders[0].PendingOrdersList[key].forEach((a) => {
                tradeProcessRequest.PurchasedFunds.forEach((b) => {
                  if (a.ISIN === b.ISIN && a.ORDER_DATE === new Date().toLocaleDateString('en-CA').toString()) {
                    if (ISIN.length === 0) {
                      this.$store.dispatch('createLog', 'Existing pending order found with same product.');
                      ISIN += a.ISIN;
                    } else {
                      ISIN += ',' + a.ISIN;
                    }
                  }
                });
              });
          } catch { /* catch */ }
        }
      }
      if (ISIN.length > 0) { // have duplicate orders
        if (confirm('You have already submitted order/s for ISIN/s: ' + ISIN + ' in the last 24 hours. \n\nAre you sure you wish to process another order? If you do submit multiple orders, Capital Platforms will not be liable for any losses incurred as a consequence of order duplication.\n\nPlease contact client servicing should you have any questions.\n\n')) {
        // set type
          this.$store.dispatch('createLog', 'Pop-up warning message was confirmed by user. Proceeding to process transaction.');
          this.$store.dispatch('placeBuyOrder', tradeProcessRequest);
        } else {
          this.$store.dispatch('createLog', 'Pop-up warning message was cancelled by user.');
          this.loading = false;
        }
      } else { // normal process
        this.$store.dispatch('placeBuyOrder', tradeProcessRequest);
      }
    });
    this.$store.dispatch('createLog', 'Finished executing placeMultipleBuyOrder...');
  }

  public placeBuyOrder() {
    const tradeProcessRequest: any = this.tradeProcessRequest;
    const alertGainAction = () => {
      if (this.alertGainAction === 1) { return 'AutoEmail'; }
      if (this.alertGainAction === 3) { return 'AutoReset'; } else {
        return 'No Alert';
      }
    };
    const alertFallAction = () => {
      if (this.alertFallAction === 1) { return 'AutoEmail'; }
      if (this.alertFallAction === 3) { return 'AutoReset'; } else {
        return 'No Alert';
      }
    };
    let product: any = null;
    if (this.checkoutProduct) {
      product = {
        ID: this.checkoutProduct.ID,
        CartId: this.checkoutProduct.ID,
        Name: this.checkoutProduct.Name,
        ISIN: this.checkoutProduct.ISIN,
        NAV: this.checkoutProduct.NAV,
        TradeValue: this.checkoutProduct.TradeValue,
        ProductType: this.checkoutProduct.ProductType,
        Currency: this.checkoutProduct.Currency,
        CashRebate: this.checkoutProduct.CashRebate,
        DiscountPercentage: this.checkoutProduct.DiscountPercentage,
        CashRebateType: this.checkoutProduct.CashRebateType,
        CheckoutOnline: this.checkoutProduct.CheckoutOnline,
        CheckoutDocusign: this.checkoutProduct.CheckoutDocusign,
        TradeOptions: this.checkoutProduct.TradeOptions,
        Price: (this.checkoutProduct.NAV != null && this.checkoutProduct.NAV !== 0) ? this.checkoutProduct.NAV : 1,
        Units: this.checkoutProduct.TradeOptions === 'Units' ? this.checkoutProduct.TradeValue : 0,
        Amount: this.checkoutProduct.TradeOptions === 'Amount' ? this.checkoutProduct.TradeValue : 0,
        isFilled: true,
        Forex: [
        ],
      };
      this.sortedForex.forEach((currency: any) => { product.Forex.push(currency); });

      if (this.hasAlerts) {
        product.Alert = {
          CartId: this.checkoutProduct.ID,
          GainRate: this.gainTarget,
          GainSelection: alertGainAction(),
          FallRate: parseFloat('-' + this.fallTarget),
          FallSelection: alertFallAction(),
          AutoResetGainFallValue: parseFloat('-' + this.afterResetGainTarget),
        };
      } else {
        product.Alert = {};
      }

      // sets active rebate type by removing value that isn't needed
      if (this.checkoutProduct.CashRebateType) {
        if (this.checkoutProduct.CashRebateType.value === 'Cash') {
          product.DiscountPercentage = null;
        }
        if (this.checkoutProduct.CashRebateType.value === 'Units') {
          product.CashRebate = null;
        }
      }
      // Check and replace (update) line item in array if same already ID exists
      //  Check if  active product already exists
      if ( this.tradeProcessRequest.PurchasedFunds.filter((item: any) => item.ID === product.ID ).length > 0) {
        const tmpArr: any[] = [
          product,
        ];

        const filteredArray = this.tradeProcessRequest.PurchasedFunds.map((obj: any) => tmpArr.find((o: any) => o.ID === obj.ID) || obj);
        this.tradeProcessRequest.PurchasedFunds = filteredArray;
      } else {
        this.tradeProcessRequest.PurchasedFunds.push(product);
      }
    }

    // Replace existing cart items with checkout item
    // find index
    const index = this.activeBuyOrders.findIndex((item: any) => item.ID === this.checkoutProduct.ID);
    // replace with new obj
    this.activeBuyOrders[index] = product;
    if ((index + 1) < this.activeBuyOrders.length) {
      this.resetCheckoutProduct(this.useSameValues);
      this.startCheckout(this.activeBuyOrders[index + 1]);
      this.activeCheckoutStep = 1;
      this.activeProductCount++;
    } else {
      this.activeCheckoutStep = 4;
    }
  }

  // Used to sell individual Item
  public placeSellOrder() {
    const tradeProcessRequest = this.tradeProcessRequest;
    if (this.checkoutProduct) {
      const product = {
        CartId: this.checkoutProduct.ID,
        ISIN: this.checkoutProduct.ISIN,
        Currency: this.checkoutProduct.Currency,
        TradeOptions: this.checkoutProduct.TradeOptions,
        Price: this.checkoutProduct.NAV,
        Units: this.checkoutProduct.TradeValue,
        Amount: this.checkoutProduct.TradeValue * this.checkoutProduct.NAV,
        DiscountPercentage: 0,
      };
      // @ts-ignore
      tradeProcessRequest.SoldFunds.push(product);
    }

    this.loading = true;
    this.$store.dispatch('placeSellOrder', tradeProcessRequest);
  }

  // New Sell Order function that takes multiple products
  // This can be used to checkout manually (pdf), via Docusign (email) or automatically online
  @Debounce(1000)
  public placeMultipleSellOrder(online: boolean, type: string) {
    this.loading = true;
    // set type
    this.checkoutType = type;

    const formattedSoldFunds: any[] = [];

    this.activeSellOrders.forEach((item) => {
      const formattedItem: any = {
        CartId: item.ID,
        ISIN: item.ISIN,
        Currency: item.Currency,
        TradeOptions: item.TradeOptions,
        Price: item.NAV,
        Units: item.TradeValue,
        Amount: item.TradeOptions === 'SellAll' ? '100%' : item.TradeValue * item.NAV,
        DiscountPercentage: 0,
      };
      formattedSoldFunds.push(formattedItem);
    });
    const availableCash = this.$store.state.account.details?.activeAccount?.AvailableBalance;
    const pendingOrdersAmount = this.$store.state.account.details?.activeAccount?.PendingOrderAmount;
    this.$store.dispatch('createLog', `Executing placeMultipleSellOrder...AvailableCash=${availableCash} PendingOrdersAmount=${pendingOrdersAmount}`);
    this.$store.dispatch('getPendingOrders').then(() => {
      const tradeProcessRequest: any = this.tradeProcessRequest;
      if (online) {
        if (this.manualCheckoutOnly) {
          this.$store.dispatch('createLog', 'Online checkout blocked (manualCheckoutOnly). Forcing CheckoutOnline=false.');
          tradeProcessRequest.CheckoutOnline = false;
          this.loading = false;
          return;
        }
        tradeProcessRequest.CheckoutOnline = true;
      }
      if (this.checkoutType === 'docusign') { tradeProcessRequest.CheckoutDocusign = true; }
      this.tradeProcessRequest.SoldFunds = formattedSoldFunds;
      let ISIN = '';
      if (this.$store.state.account.pendingOrders && this.$store.state.account.pendingOrders.length > 0) {
        for (const [key, value] of Object.entries(this.$store.state.account.pendingOrders[0].PendingOrdersList)) {
          try {
              this.$store.state.account.pendingOrders[0].PendingOrdersList[key].forEach((a) => {
                tradeProcessRequest.SoldFunds.forEach((b) => {
                  if (a.ISIN === b.ISIN && a.ORDER_DATE === new Date().toLocaleDateString('en-CA').toString()) {
                    if (ISIN.length === 0) {
                      this.$store.dispatch('createLog', 'Existing pending order found with same product.');
                      ISIN += a.ISIN;
                    } else {
                      ISIN += ',' + a.ISIN;
                    }
                  }
                });
              });
          } catch { /* catch */ }
        }
      }
      if ( ISIN.length > 0) {// have duplicate sell orders
        if (confirm('You have already submitted order/s for ISIN/s: ' + ISIN + ' in the last 24 hours. \n\nAre you sure you wish to process another order? If you do submit multiple orders, Capital Platforms will not be liable for any losses incurred as a consequence of order duplication.\n\nPlease contact client servicing should you have any questions.\n\n') ) {
          this.$store.dispatch('createLog', 'Pop-up warning message was confirmed by user. Proceeding to process transaction.');
          this.$store.dispatch('placeBuyOrder', tradeProcessRequest);
        } else {
          this.$store.dispatch('createLog', 'Pop-up warning message was cancelled by user.');
          this.loading = false;
        }
      } else {
        this.$store.dispatch('placeBuyOrder', tradeProcessRequest);
      }
    });
    this.$store.dispatch('createLog', 'Finished executing placeMultipleSellOrder...');
  }

  public resetCheckoutProduct(useSameValues: boolean) {
    this.amount = 0;
    this.checkoutProduct = null;
    this.activeCheckoutStep = 1;
    this.prevCheckoutStep = 0;
    this.sortedCashPosition.forEach((item) => {
      item.value = 0;
      item.valueInProductCurrency = 0;
    });

    if (!useSameValues) {
      // alerts
      this.gainTarget = 5;
      this.alertGainAction = 1;
      this.afterResetGainTarget = 5;
      this.fallTarget = 5;
      this.alertFallAction = 1;
      this.afterResetFallTarget = 5;
    }
  }

  @Debounce(500)
  public checkCartItems() {
    if (!this.cartList) return;
    this.itemsToRemove = this.cartList.filter((item: any) => !item.NAV && !this.isBondOrNote(item) && item.TradeOptions !== 'Amount');
    if (this.itemsToRemove.length > 0) {
      this.removeItemsWithNullNav();
    }
  }

  public async removeItemsWithNullNav() {
    try {
      for (const item of this.itemsToRemove) {
        await this.$store.dispatch('deleteCart', {
          name: item.Name,
          cartID: item.ID,
          roleInfoId: item.UserInfoId.toString(),
          disableSnackbar: true,
        });
      }
      this.$store.commit('toggleBasketModal', true);
    } catch (error) {
      this.$store.dispatch('createLog', `Failed to remove items with null NAV ${error}`);
    } finally {
      this.itemsToRemove = [];
    }
  }

  public async mounted() {
    await this.$store.dispatch('getCart');
    if (this.$store.state.account.details.role.includes('client')) {
      this.$store.dispatch('getCashPosition');
    }
    this.checkCartItems();
  }

  public beforeDestroy() {
    this.closeCheckout();
    this.$store.dispatch('getPendingOrders');
    this.$store.dispatch('getAccountSummary', { ids: this.accountIds, activeAccount: this.$store.state.account.details.activeAccountId } );
    this.$store.commit('setCheckoutActive', false);
    this.$store.commit('updateTradeResolved', null);
  }
}
